import {
  downloadSheetForApi,
  shareSheetForApi,
  type ShareVisibility,
} from './exportSheetForApi';
import { readSheetForApi } from './readSheetForApi';
import type {
  ActivateSheetResult,
  CreateSheetResult,
  CreateSnapshotResult,
  DeleteSheetResult,
  ReadSheetResult,
  SheetApiContext,
  SheetApiError,
  WriteSheetResult,
} from './types';
import type { Sheet } from '../../types';

export type SheetApiHostDeps = {
  getState: () => { sheets: Sheet[]; activeSheetId: string };
  createSheet: (options?: {
    title?: string;
    activate?: boolean;
  }) => Promise<CreateSheetResult | SheetApiError>;
  applySheetWrite: (
    sheetId: string,
    sheet: Sheet,
    options?: { expectedRevision?: number; replaceSheet?: boolean },
  ) => Promise<WriteSheetResult | SheetApiError>;
  deleteSheet: (
    sheetId: string,
    options?: { expectedRevision?: number },
  ) => Promise<DeleteSheetResult | SheetApiError>;
  activateSheet: (sheetId: string) => Promise<ActivateSheetResult | SheetApiError>;
  tidySheet: (
    sheetId: string,
    options?: { expectedRevision?: number },
  ) => Promise<WriteSheetResult | SheetApiError>;
  addLayer: (
    sheetId: string,
    options?: { name?: string; expectedRevision?: number },
  ) => Promise<WriteSheetResult | SheetApiError>;
  deleteLayer: (
    sheetId: string,
    layerId: string,
    options?: { expectedRevision?: number },
  ) => Promise<WriteSheetResult | SheetApiError>;
  createSnapshot: (
    sheetId: string,
    options?: { name?: string; description?: string; expectedRevision?: number },
  ) => Promise<CreateSnapshotResult | SheetApiError>;
  undoSheet: (
    sheetId: string,
    options?: { expectedRevision?: number },
  ) => Promise<WriteSheetResult | SheetApiError>;
  redoSheet: (
    sheetId: string,
    options?: { expectedRevision?: number },
  ) => Promise<WriteSheetResult | SheetApiError>;
  readSheet: (sheetId: string) => ReadSheetResult | SheetApiError;
  getContext: () => SheetApiContext;
};

function matchSheetRoute(pathname: string): { sheetId: string } | null {
  const match = pathname.match(/^\/v1\/sheets\/([^/]+)$/);
  if (!match) {
    return null;
  }
  return { sheetId: decodeURIComponent(match[1]) };
}

function matchSheetTidyRoute(pathname: string): { sheetId: string } | null {
  const match = pathname.match(/^\/v1\/sheets\/([^/]+)\/tidy$/);
  if (!match) {
    return null;
  }
  return { sheetId: decodeURIComponent(match[1]) };
}

function matchSheetActivateRoute(pathname: string): { sheetId: string } | null {
  const match = pathname.match(/^\/v1\/sheets\/([^/]+)\/activate$/);
  if (!match) {
    return null;
  }
  return { sheetId: decodeURIComponent(match[1]) };
}

function matchSheetLayersRoute(pathname: string): { sheetId: string } | null {
  const match = pathname.match(/^\/v1\/sheets\/([^/]+)\/layers$/);
  if (!match) {
    return null;
  }
  return { sheetId: decodeURIComponent(match[1]) };
}

function matchSheetLayerRoute(
  pathname: string,
): { sheetId: string; layerId: string } | null {
  const match = pathname.match(/^\/v1\/sheets\/([^/]+)\/layers\/([^/]+)$/);
  if (!match) {
    return null;
  }
  return {
    sheetId: decodeURIComponent(match[1]),
    layerId: decodeURIComponent(match[2]),
  };
}

function matchSheetSnapshotsRoute(pathname: string): { sheetId: string } | null {
  const match = pathname.match(/^\/v1\/sheets\/([^/]+)\/snapshots$/);
  if (!match) {
    return null;
  }
  return { sheetId: decodeURIComponent(match[1]) };
}

function matchSheetHistoryRoute(
  pathname: string,
): { sheetId: string; direction: 'undo' | 'redo' } | null {
  const match = pathname.match(/^\/v1\/sheets\/([^/]+)\/(undo|redo)$/);
  if (!match) {
    return null;
  }
  return {
    sheetId: decodeURIComponent(match[1]),
    direction: match[2] as 'undo' | 'redo',
  };
}

function matchSheetShareRoute(pathname: string): { sheetId: string } | null {
  const match = pathname.match(/^\/v1\/sheets\/([^/]+)\/share$/);
  if (!match) {
    return null;
  }
  return { sheetId: decodeURIComponent(match[1]) };
}

function matchSheetDownloadRoute(pathname: string): { sheetId: string } | null {
  const match = pathname.match(/^\/v1\/sheets\/([^/]+)\/download$/);
  if (!match) {
    return null;
  }
  return { sheetId: decodeURIComponent(match[1]) };
}

function findSheet(deps: SheetApiHostDeps, sheetId: string): Sheet | undefined {
  return deps.getState().sheets.find(sheet => sheet.id === sheetId);
}

function parseShareVisibility(body: unknown): ShareVisibility | SheetApiError {
  if (!isRecord(body) || typeof body.visibility !== 'string') {
    return {
      ok: false,
      code: 'VALIDATION_ERROR',
      message: 'Request body must include visibility ("private" | "public")',
    };
  }
  if (body.visibility !== 'private' && body.visibility !== 'public') {
    return {
      ok: false,
      code: 'VALIDATION_ERROR',
      message: 'visibility must be "private" or "public"',
    };
  }
  return body.visibility;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function expectedRevisionFromBody(body: unknown): number | undefined {
  if (!isRecord(body) || body.expectedRevision === undefined) {
    return undefined;
  }
  const expectedRevision = Number(body.expectedRevision);
  return Number.isNaN(expectedRevision) ? undefined : expectedRevision;
}

export class SheetApiHost {
  constructor(private readonly deps: SheetApiHostDeps) {}

  async handleRequest(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<{ status: number; body: unknown }> {
    if (path === '/v1/context' && method === 'GET') {
      const context = this.deps.getContext();
      return {
        status: 200,
        body: { ok: true, ...context },
      };
    }

    if (path === '/v1/sheets' && method === 'POST') {
      const options = isRecord(body) ? body : undefined;
      const title =
        typeof options?.title === 'string' ? options.title.trim() : undefined;
      const activate =
        options?.activate === undefined ? true : Boolean(options.activate);

      const result = await this.deps.createSheet({
        title: title || undefined,
        activate,
      });

      if (!result.ok) {
        const status =
          result.code === 'SHEET_LIMIT_REACHED'
            ? 403
            : result.code === 'VALIDATION_ERROR'
              ? 400
              : 400;
        return { status, body: result };
      }

      return {
        status: 201,
        body: {
          ok: true,
          sheetId: result.sheetId,
          revision: result.revision,
          sheet: readSheetForApi(result.sheet).sheet,
        },
      };
    }

    const sheetRoute = matchSheetRoute(path);
    if (sheetRoute && method === 'GET') {
      const result = this.deps.readSheet(sheetRoute.sheetId);
      if (!result.ok) {
        const status = result.code === 'SHEET_NOT_FOUND' ? 404 : 400;
        return { status, body: result };
      }
      return { status: 200, body: result };
    }

    if (sheetRoute && method === 'PUT') {
      if (!isRecord(body) || body.sheet == null) {
        return {
          status: 400,
          body: {
            ok: false,
            code: 'VALIDATION_ERROR',
            message: 'Request body must include sheet',
          } satisfies SheetApiError,
        };
      }

      const expectedRevision =
        body.expectedRevision !== undefined
          ? Number(body.expectedRevision)
          : undefined;
      const replaceSheet = body.replaceSheet === true;

      const result = await this.deps.applySheetWrite(
        sheetRoute.sheetId,
        body.sheet as Sheet,
        {
          ...(expectedRevision !== undefined && !Number.isNaN(expectedRevision)
            ? { expectedRevision }
            : {}),
          ...(replaceSheet ? { replaceSheet: true } : {}),
        },
      );

      if (!result.ok) {
        const status =
          result.code === 'REVISION_CONFLICT'
            ? 409
            : result.code === 'SHEET_NOT_FOUND'
              ? 404
              : result.code === 'SHEET_ID_MISMATCH'
                ? 400
                : 400;
        return { status, body: result };
      }

      return {
        status: 200,
        body: {
          ok: true,
          sheetId: result.sheetId,
          revision: result.revision,
          sheet: readSheetForApi(result.sheet).sheet,
        },
      };
    }

    if (sheetRoute && method === 'DELETE') {
      const result = await this.deps.deleteSheet(sheetRoute.sheetId, {
        expectedRevision: expectedRevisionFromBody(body),
      });

      if (!result.ok) {
        const status =
          result.code === 'REVISION_CONFLICT'
            ? 409
            : result.code === 'SHEET_NOT_FOUND'
              ? 404
              : 400;
        return { status, body: result };
      }

      return { status: 200, body: result };
    }

    const activateRoute = matchSheetActivateRoute(path);
    if (activateRoute && method === 'POST') {
      const result = await this.deps.activateSheet(activateRoute.sheetId);
      if (!result.ok) {
        const status = result.code === 'SHEET_NOT_FOUND' ? 404 : 400;
        return { status, body: result };
      }
      return { status: 200, body: result };
    }

    const tidyRoute = matchSheetTidyRoute(path);
    if (tidyRoute && method === 'POST') {
      const expectedRevision = expectedRevisionFromBody(body);

      const result = await this.deps.tidySheet(
        tidyRoute.sheetId,
        expectedRevision !== undefined
          ? { expectedRevision }
          : undefined,
      );

      if (!result.ok) {
        const status =
          result.code === 'REVISION_CONFLICT'
            ? 409
            : result.code === 'SHEET_NOT_FOUND'
              ? 404
              : 400;
        return { status, body: result };
      }

      return {
        status: 200,
        body: {
          ok: true,
          sheetId: result.sheetId,
          revision: result.revision,
          sheet: readSheetForApi(result.sheet).sheet,
        },
      };
    }

    const layersRoute = matchSheetLayersRoute(path);
    if (layersRoute && method === 'POST') {
      const options = isRecord(body) ? body : {};
      const result = await this.deps.addLayer(layersRoute.sheetId, {
        name: typeof options.name === 'string' ? options.name.trim() : undefined,
        expectedRevision: expectedRevisionFromBody(body),
      });

      if (!result.ok) {
        const status =
          result.code === 'REVISION_CONFLICT'
            ? 409
            : result.code === 'SHEET_NOT_FOUND'
              ? 404
              : 400;
        return { status, body: result };
      }

      return {
        status: 200,
        body: {
          ok: true,
          sheetId: result.sheetId,
          revision: result.revision,
          activeLayerId: result.sheet.activeLayerId,
          sheet: readSheetForApi(result.sheet).sheet,
        },
      };
    }

    const layerRoute = matchSheetLayerRoute(path);
    if (layerRoute && method === 'DELETE') {
      const result = await this.deps.deleteLayer(
        layerRoute.sheetId,
        layerRoute.layerId,
        { expectedRevision: expectedRevisionFromBody(body) },
      );

      if (!result.ok) {
        const status =
          result.code === 'REVISION_CONFLICT'
            ? 409
            : result.code === 'SHEET_NOT_FOUND' || result.code === 'LAYER_NOT_FOUND'
              ? 404
              : 400;
        return { status, body: result };
      }

      return {
        status: 200,
        body: {
          ok: true,
          sheetId: result.sheetId,
          revision: result.revision,
          activeLayerId: result.sheet.activeLayerId,
          sheet: readSheetForApi(result.sheet).sheet,
        },
      };
    }

    const snapshotsRoute = matchSheetSnapshotsRoute(path);
    if (snapshotsRoute && method === 'POST') {
      const options = isRecord(body) ? body : {};
      const result = await this.deps.createSnapshot(snapshotsRoute.sheetId, {
        name: typeof options.name === 'string' ? options.name.trim() : undefined,
        description:
          typeof options.description === 'string'
            ? options.description.trim()
            : undefined,
        expectedRevision: expectedRevisionFromBody(body),
      });

      if (!result.ok) {
        const status =
          result.code === 'REVISION_CONFLICT'
            ? 409
            : result.code === 'SHEET_NOT_FOUND'
              ? 404
              : 400;
        return { status, body: result };
      }

      return {
        status: 201,
        body: {
          ok: true,
          sheetId: result.sheetId,
          revision: result.revision,
          snapshotId: result.snapshotId,
          sheet: readSheetForApi(result.sheet).sheet,
        },
      };
    }

    const historyRoute = matchSheetHistoryRoute(path);
    if (historyRoute && method === 'POST') {
      const expectedRevision = expectedRevisionFromBody(body);

      const navigate =
        historyRoute.direction === 'undo'
          ? this.deps.undoSheet
          : this.deps.redoSheet;

      const result = await navigate(
        historyRoute.sheetId,
        expectedRevision !== undefined
          ? { expectedRevision }
          : undefined,
      );

      if (!result.ok) {
        const status =
          result.code === 'REVISION_CONFLICT'
            ? 409
            : result.code === 'SHEET_NOT_FOUND'
              ? 404
              : result.code === 'HISTORY_NAVIGATION_UNAVAILABLE'
                ? 409
                : 400;
        return { status, body: result };
      }

      return {
        status: 200,
        body: {
          ok: true,
          sheetId: result.sheetId,
          revision: result.revision,
          historyIndex: result.sheet.historyIndex,
          sheet: readSheetForApi(result.sheet).sheet,
        },
      };
    }

    const shareRoute = matchSheetShareRoute(path);
    if (shareRoute && method === 'POST') {
      const visibility = parseShareVisibility(body);
      if (typeof visibility !== 'string') {
        return { status: 400, body: visibility };
      }

      const sheet = findSheet(this.deps, shareRoute.sheetId);
      if (!sheet) {
        return {
          status: 404,
          body: {
            ok: false,
            code: 'SHEET_NOT_FOUND',
            message: `Sheet not found: ${shareRoute.sheetId}`,
          } satisfies SheetApiError,
        };
      }

      const result = await shareSheetForApi(sheet, visibility);
      if (!result.ok) {
        const status =
          result.code === 'SHARE_UPLOAD_FAILED' ? 502 : 400;
        return { status, body: result };
      }
      return { status: 200, body: result };
    }

    const downloadRoute = matchSheetDownloadRoute(path);
    if (downloadRoute && method === 'GET') {
      const sheet = findSheet(this.deps, downloadRoute.sheetId);
      if (!sheet) {
        return {
          status: 404,
          body: {
            ok: false,
            code: 'SHEET_NOT_FOUND',
            message: `Sheet not found: ${downloadRoute.sheetId}`,
          } satisfies SheetApiError,
        };
      }
      return { status: 200, body: downloadSheetForApi(sheet) };
    }

    return {
      status: 404,
      body: {
        ok: false,
        code: 'NOT_FOUND',
        message: 'Route not found',
      },
    };
  }
}
